import React, { useEffect, useState } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  PinterestIcon,
  PinterestShareButton,
  PocketIcon,
  PocketShareButton,
  TwitterIcon,
  TwitterShareButton,
} from 'react-share';
import clsx from 'clsx';
import styles from './SocialShare.module.scss';
import { ShareCircle } from '../../atoms/icons';
import { useUrl } from '../../../../../providers/url';
import { usePageLoading } from '@frontastic/catwalk/js/helper/hooks/usePageLoading';

interface Product {
  name: string;
  variants?: Array<{
    images?: Array<{
      url: string;
    }>;
  }>;
}

interface ButtonProps {
  url: string;
  size: number;
  title: string;
  beforeOnClick: () => void;
  media?: string;
}

interface SocialShareProps {
  product: Product;
  isInverted?: boolean;
  size?: number;
  showShareIcon?: boolean;
  image?: string;
  buttons?: string[];
  type?: 'product' | 'journal';
}

const SOCIAL_BUTTONS: Record<string, React.FC<ButtonProps>> = {
  Email: (props) => (
    <EmailShareButton {...props} subject={props.title} beforeOnClick={props.beforeOnClick}>
      <EmailIcon size={props.size} round />
    </EmailShareButton>
  ),
  Facebook: (props) => (
    <FacebookShareButton {...props}>
      <FacebookIcon size={props.size} round />
    </FacebookShareButton>
  ),
  Pinterest: (props) => (
    <PinterestShareButton {...props} media={props.media || ''}>
      <PinterestIcon size={props.size} round />
    </PinterestShareButton>
  ),
  'Pocket share': (props) => (
    <PocketShareButton {...props}>
      <PocketIcon size={props.size} round />
    </PocketShareButton>
  ),
  Twitter: (props) => (
    <TwitterShareButton {...props}>
      <TwitterIcon size={props.size} round />
    </TwitterShareButton>
  ),
};

const useNativeShare = (shareData: { title: string; url: string }) => {
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    if (navigator?.canShare?.(shareData)) {
      setCanShare(true);
    }
  }, [shareData]);

  const handleSharing = async () => {
    try {
      await navigator.share(shareData);
    } catch (err) {
      console.error(err.name, err.message);
    }
  };

  return { canShare, handleSharing };
};

const SocialShare: React.FC<SocialShareProps> = ({
  product,
  isInverted = false,
  size = 25,
  showShareIcon = false,
  image = '',
  buttons = [],
  type = 'product',
}) => {
  const { url } = useUrl();
  const shareData = { title: product.name, url };
  const { canShare, handleSharing } = useNativeShare(shareData);
  const { suppressEventListeners } = usePageLoading();

  const renderSocialButtons = () => {
    const commonProps = {
      url,
      size,
      title: product.name,
      beforeOnClick: () => {
        suppressEventListeners();
      },
    };

    const allButtons = Object.values(SOCIAL_BUTTONS).map((Button, index) => (
      <Button key={index} {...commonProps} media={product.variants?.[0].images?.[0]?.url || image} />
    ));

    if (type === 'journal' && buttons.length > 0) {
      return buttons.map((buttonName) => {
        const Button = SOCIAL_BUTTONS[buttonName];
        if (!Button) {
          console.warn(`Unknown social button: ${buttonName}`);
          return null;
        }
        return (
          <Button
            key={buttonName}
            {...commonProps}
            media={buttonName === 'Pinterest' ? product.variants?.[0].images?.[0]?.url || image : undefined}
          />
        );
      });
    }

    return allButtons;
  };

  return (
    <div className={clsx(styles.root, { [styles.inverted]: isInverted })}>
      {renderSocialButtons()}
      {showShareIcon && canShare && (
        <button className={styles.shareButton} onClick={handleSharing}>
          <ShareCircle size={48} />
        </button>
      )}
    </div>
  );
};

export default SocialShare;
